/**
 * Auth Store - Zustand
 * State management for Authentication and Session
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { AuthUser, AuthSession } from '@/types/auth.types'
import { AUTH_STORAGE_KEYS, INITIAL_AUTH_SESSION } from '@/types/auth.types'
import { mockLogin, mockValidateToken } from '@/data/mockUsers'
import { getPermissionsForRole } from '@/data/mockPermissions'
import { addAuditEntry } from '@/data/mockAuditLog'

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface AuthState extends AuthSession {
  // Additional UI state
  isInitialized: boolean
  rememberMe: boolean
}

interface AuthActions {
  // Auth operations
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: (allDevices?: boolean) => Promise<void>
  refreshSession: () => Promise<void>

  // Session management
  initializeAuth: () => Promise<void>
  clearError: () => void

  // Helpers
  hasRole: (roles: string | string[]) => boolean
  hasPermission: (permission: string) => boolean

  // Reset
  resetStore: () => void
}

type AuthStore = AuthState & AuthActions

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: AuthState = {
  ...INITIAL_AUTH_SESSION,
  isInitialized: false,
  rememberMe: false,
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ========================================
        // AUTH OPERATIONS
        // ========================================

        login: async (email: string, password: string, rememberMe = false) => {
          set({ isLoading: true, error: null })

          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 500))

          const result = mockLogin(email, password)

          if (!result) {
            // Log failed login
            addAuditEntry({
              action: 'login_failed',
              resourceType: 'session',
              details: {
                attemptedEmail: email,
                reason: 'Invalid credentials',
              },
              success: false,
              errorMessage: 'Invalid email or password',
            })

            set({
              isLoading: false,
              error: 'Invalid email or password',
            })
            return
          }

          const { user, token, refreshToken, sessionId } = result

          // Log successful login
          addAuditEntry({
            userId: user.id,
            userEmail: user.email,
            performedByName: user.fullName,
            action: 'login',
            resourceType: 'session',
            details: {
              loginMethod: 'password',
              sessionId,
            },
            success: true,
          })

          // Store in localStorage if remember me
          if (rememberMe) {
            localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token)
            localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
            localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
            localStorage.setItem(AUTH_STORAGE_KEYS.SESSION_ID, sessionId)
            localStorage.setItem(AUTH_STORAGE_KEYS.REMEMBER_ME, 'true')
          } else {
            // Use sessionStorage for session-only
            sessionStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token)
            sessionStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
            sessionStorage.setItem(AUTH_STORAGE_KEYS.SESSION_ID, sessionId)
          }

          set({
            user,
            token,
            refreshToken,
            sessionId,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            rememberMe,
            isInitialized: true,
          })
        },

        logout: async (allDevices = false) => {
          const { user, sessionId } = get()

          // Log logout
          if (user) {
            addAuditEntry({
              userId: user.id,
              userEmail: user.email,
              performedByName: user.fullName,
              action: allDevices ? 'all_sessions_terminated' : 'logout',
              resourceType: 'session',
              details: {
                sessionId: sessionId ?? null,
                ...(allDevices ? {} : { terminatedSessionCount: 1 }),
              },
              success: true,
            })
          }

          // Clear all storage
          localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN)
          localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
          localStorage.removeItem(AUTH_STORAGE_KEYS.USER)
          localStorage.removeItem(AUTH_STORAGE_KEYS.SESSION_ID)
          localStorage.removeItem(AUTH_STORAGE_KEYS.REMEMBER_ME)
          sessionStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN)
          sessionStorage.removeItem(AUTH_STORAGE_KEYS.USER)
          sessionStorage.removeItem(AUTH_STORAGE_KEYS.SESSION_ID)

          set({
            ...INITIAL_AUTH_SESSION,
            isInitialized: true,
            rememberMe: false,
          })
        },

        refreshSession: async () => {
          const { refreshToken, user } = get()

          if (!refreshToken || !user) {
            return
          }

          // In real app, would call refresh token API
          // For mock, just simulate
          await new Promise(resolve => setTimeout(resolve, 300))

          const newToken = `mock-token-${user.id}-${Date.now()}`

          set({ token: newToken })

          // Update storage
          const rememberMe = localStorage.getItem(AUTH_STORAGE_KEYS.REMEMBER_ME) === 'true'
          if (rememberMe) {
            localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, newToken)
          } else {
            sessionStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, newToken)
          }
        },

        // ========================================
        // SESSION MANAGEMENT
        // ========================================

        initializeAuth: async () => {
          // Check localStorage first (remember me)
          let token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)
          let userJson = localStorage.getItem(AUTH_STORAGE_KEYS.USER)
          let sessionId = localStorage.getItem(AUTH_STORAGE_KEYS.SESSION_ID)
          const rememberMe = localStorage.getItem(AUTH_STORAGE_KEYS.REMEMBER_ME) === 'true'

          // Fall back to sessionStorage
          if (!token) {
            token = sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)
            userJson = sessionStorage.getItem(AUTH_STORAGE_KEYS.USER)
            sessionId = sessionStorage.getItem(AUTH_STORAGE_KEYS.SESSION_ID)
          }

          if (token && userJson) {
            // Validate token
            const validatedUser = mockValidateToken(token)

            if (validatedUser) {
              set({
                user: validatedUser,
                token,
                sessionId,
                isAuthenticated: true,
                isLoading: false,
                isInitialized: true,
                rememberMe,
              })
              return
            }
          }

          // No valid session
          set({
            ...INITIAL_AUTH_SESSION,
            isLoading: false,
            isInitialized: true,
          })
        },

        clearError: () => {
          set({ error: null })
        },

        // ========================================
        // HELPERS
        // ========================================

        hasRole: (roles: string | string[]) => {
          const { user } = get()
          if (!user) return false

          const roleArray = Array.isArray(roles) ? roles : [roles]
          return roleArray.includes(user.role)
        },

        hasPermission: (permission: string) => {
          const { user } = get()
          if (!user) return false

          const permissions = getPermissionsForRole(user.role).map(item => item.permissionKey)

          return (
            permissions.includes(permission) ||
            permissions.some((p: string) => {
              if (p.endsWith('.*')) {
                const prefix = p.slice(0, -1)
                return permission.startsWith(prefix)
              }
              return false
            })
          )
        },

        // ========================================
        // RESET
        // ========================================

        resetStore: () => {
          set(initialState)
        },
      }),
      {
        name: 'taxhive-auth-store',
        partialize: state => ({
          // Don't persist sensitive data - handled separately
          rememberMe: state.rememberMe,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
)

// ============================================================================
// SELECTORS
// ============================================================================

export const selectUser = (state: AuthStore) => state.user
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated
export const selectIsLoading = (state: AuthStore) => state.isLoading
export const selectError = (state: AuthStore) => state.error
export const selectIsInitialized = (state: AuthStore) => state.isInitialized
export const selectSessionId = (state: AuthStore) => state.sessionId

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Check if user is logged in
 */
export function useIsLoggedIn(): boolean {
  return useAuthStore(state => state.isAuthenticated && !!state.user)
}

/**
 * Get current user
 */
export function useCurrentUser(): AuthUser | null {
  return useAuthStore(selectUser)
}

/**
 * Get current user's role
 */
export function useCurrentRole(): string | null {
  const user = useAuthStore(selectUser)
  return user?.role ?? null
}

/**
 * Check if current user has a specific role
 */
export function useHasRole(roles: string | string[]): boolean {
  const user = useAuthStore(selectUser)
  if (!user) return false

  const roleArray = Array.isArray(roles) ? roles : [roles]
  return roleArray.includes(user.role)
}
